import React from 'react';
import type { NextPage } from 'next';
import Head from 'next/head';
import _ from 'lodash';
import he from 'he';
import { GeoData, web } from '@wix/wix-common-aspects';
import { WixDesignSystemProvider } from '@wix/design-system';
import { Service } from '@wix/ambassador-category-services/types';
import {
  WixNextApiRequest,
  WixNextApiResponse,
  IParams,
  LocaleDataTranslations,
  SEOProps,
  PartnerProfileEnriched,
} from '@common/types';
import { PROFILE_NOT_FOUND } from '@utils/constants';
import createProvidersAPI from '@services/ProvidersAPI';
import createServicesAPI from '@services/ServicesAPI';
import createSeoAPI from '@services/ProfileSeoAPI';
import createLanguagesAPI from '@services/LanguagesAPI';
import createUserAPI from '@services/UserAPI';
import createCurrencyAPI from '@services/CurrencyAPI';
import { Language } from '@common/types';
import { ProfileContextProvider } from '@components/Contexts/ProfileContext';
import { HeaderContextProvider } from '@components/Contexts/HeaderContext';
import { BiSessionContextProvider } from '@components/Contexts/BiSessionContext';
import Profile from '@components/Profile';
import NotFound from '@components/Profile/NotFound';

import {
  getCurrencyAndConversionRate,
  getCurrencyPreferences,
} from '../hire/[[...all]]';

const WixPartner: NextPage<{
  partnerProfile: PartnerProfileEnriched;
  services: Array<Service>;
  geo: GeoData;
  language: string;
  languages: Array<Language>;
  userDetails: any;
  userProfileImage: string;
  origin: string;
  entry: any;
  isMyProfile: boolean;
  enabled: boolean;
  isRenderedInModal: boolean;
  isMobileUserAgent: boolean;
  localeDataTranslations: LocaleDataTranslations;
  error: string;
  host: string;
  seo: SEOProps;
  userCurrency: string;
  currencyConversionRate: number;
  currencyPreferences: string;
}> = ({
  partnerProfile,
  services,
  geo,
  language,
  languages,
  userDetails,
  userProfileImage,
  origin,
  entry,
  isMyProfile,
  enabled,
  isRenderedInModal,
  isMobileUserAgent,
  localeDataTranslations,
  host,
  error,
  seo: { title, metaTags, links, structureData, gtm },
  userCurrency,
  currencyConversionRate,
  currencyPreferences,
}) => {
  const isProd = process.env.NODE_ENV === 'production';

  const ProfileHeader = () => (
    <Head>
      <title>{title}</title>
      {_.map(links, (attr, index) => (
        <link
          hrefLang={attr.hrefLang ? attr.hrefLang : 'x-default'}
          rel={attr.rel}
          href={attr.href}
          key={`lang-${index}`}
        />
      ))}
      {_.map(metaTags, (attr, index) => (
        <meta {...attr} key={`meta-${index}`} />
      ))}
      <link
        rel="canonical"
        href={_.get(_.find(links, { rel: 'canonical' }), 'href')}
      ></link>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: structureData }}
      ></script>
      {isProd && <script dangerouslySetInnerHTML={{ __html: gtm }}></script>}
    </Head>
  );

  return (
    <>
      <ProfileHeader />
      <BiSessionContextProvider>
        <HeaderContextProvider
          services={services}
          userDetails={userDetails}
          host={host}
          userCurrency={userCurrency}
          userProfileImage={userProfileImage}
          localeDataTranslations={localeDataTranslations}
        >
          <ProfileContextProvider
            partnerProfile={partnerProfile}
            services={services}
            geo={geo}
            language={language}
            languages={languages}
            userDetails={userDetails}
            origin={origin}
            host={host}
            entry={entry}
            isMyProfile={isMyProfile}
            enabled={enabled}
            isRenderedInModal={isRenderedInModal}
            isMobileUserAgent={isMobileUserAgent}
            localeDataTranslations={localeDataTranslations}
            userCurrency={userCurrency}
            currencyConversionRate={currencyConversionRate}
            preferredCurrency={currencyPreferences}
          >
            <WixDesignSystemProvider features={{ newColorsBranding: true }}>
              {error === PROFILE_NOT_FOUND ? <NotFound /> : <Profile />}
            </WixDesignSystemProvider>
          </ProfileContextProvider>
        </HeaderContextProvider>
      </BiSessionContextProvider>
    </>
  );
};

export async function getServerSideProps({
  req,
  res,
  query,
  params,
}: {
  req: WixNextApiRequest;
  res: WixNextApiResponse;
  query: IParams;
  params: any;
}) {
  const { slug } = params;
  const isRenderedInModal = query?.inModal === 'true';
  const host = `https://${req.get('host')}`;

  const servicesAPI = createServicesAPI(res.locals.appContext, req.aspects);
  const language = req.aspects?.get(web.Language) || 'en';
  const geo = req.aspects?.get(web.Geo);
  const url = req.aspects?.get(web.Url);
  const alpha2CountryCode = geo?.['2lettersCountryCode'];

  //  move this into the try catch though... (its not in current prod)
  const services = await servicesAPI.getAllServicesWithHierarchy({
    withPathName: true,
    withTranslatedKey: true,
    language,
  });

  const currencyAPI = createCurrencyAPI(res.locals.appContext, req.aspects);
  const currencyPreferences = await getCurrencyPreferences({
    alpha2CountryCode,
    currencyAPI,
    req,
    res,
  });

  const { userCurrency, currencyConversionRate } =
    await getCurrencyAndConversionRate({
      query,
      currencyAPI,
      aspects: req.aspects,
      currencyPreferences,
      res,
    });

  const providersAPI = createProvidersAPI(res.locals.appContext, req.aspects);
  const languagesAPI = createLanguagesAPI(res.locals.appContext, req.aspects);

  try {
    const partnerProfile: any = await providersAPI.findPartnerProfileBySlug(
      he.decode(slug as string),
    );
    if (partnerProfile.error) {
      return {
        props: {
          partnerProfile: {},
          services,
          error: partnerProfile.error,
          seo: {
            title: 'Profile Not Found - Wix Marketplace',
          },
          host,
        },
      };
    }

    const { entry, origin } = query;
    const userAPI = createUserAPI(res.locals.appContext, req.aspects);
    const userDetails = await userAPI.getUserDetails();
    const userProfileImage = await userAPI.getUserProfileImage();
    const currentPartnerProfile = await providersAPI.getCurrentPartnerProfile();
    const languages = await languagesAPI.getLanguages();

    const seoAPI = await createSeoAPI({
      partnerProfile,
      language,
      url,
    });

    const props = {
      slug,
      ...(partnerProfile && {
        partnerProfile: JSON.parse(JSON.stringify(partnerProfile)),
      }),
      enabled: partnerProfile?.state?.enabled || false,
      language,
      languages,
      ...(geo && { geo }),
      services,
      ...(userDetails && { userDetails }),
      ...(userProfileImage && { userProfileImage }),
      ...(origin && { origin }),
      ...(entry && { entry }),
      isMyProfile: currentPartnerProfile?.slug === slug,
      isRenderedInModal,
      host,
      seo: {
        title: seoAPI.getPageTitle(),
        metaTags: seoAPI.getPageMetaTags(),
        links: seoAPI.getPageLinkTags(),
        structureData: JSON.parse(
          JSON.stringify(seoAPI.getProfileStructureData(partnerProfile)),
        ),
        gtm: seoAPI.getProfileGTM(),
      },
      userCurrency,
      currencyConversionRate,
      ...(currencyPreferences && { currencyPreferences }),
    };

    return {
      props,
    };
  } catch (e: any) {
    const { message, stack } = e;
    return {
      props: { message, stack, partnerProfile: {}, seo: {} },
    };
  }
}

export default WixPartner;
